import { AgCharts } from 'ag-charts-react'
import { Button } from 'primereact/button'
import { Dropdown } from 'primereact/dropdown'
import { InputNumber } from 'primereact/inputnumber'
import { InputSwitch } from 'primereact/inputswitch'
import { MultiSelect } from 'primereact/multiselect'
import { ProgressSpinner } from 'primereact/progressspinner'
import { SelectButton } from 'primereact/selectbutton'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useRecoilValue } from 'recoil'
import { useGetMonitorData } from '../../../api/report-API'
import { SelectOption } from '../../../types/types'
import { themeState } from '../../../utils/state-atoms'
import type { RunCase } from './types'

export type ChartViewSettings = {
  xZoomMode?: 'lastn' | 'minmax'
  zoomToLast?: number
  xMin?: number | null
  xMax?: number | null
  yMin?: number | null
  yMax?: number | null
}

// Fixed key order and explicit defaults so snapshots can be compared by JSON string.
const normalizeViewSettings = (s?: ChartViewSettings): Required<ChartViewSettings> => ({
  xZoomMode: s?.xZoomMode ?? 'lastn',
  zoomToLast: s?.zoomToLast ?? 0,
  xMin: s?.xMin ?? null,
  xMax: s?.xMax ?? null,
  yMin: s?.yMin ?? null,
  yMax: s?.yMax ?? null
})

export type MonitorsChartProps = {
  chartKey: string
  xIndex: number
  yIndex: number
  defaultXAxis?: string
  defaultYAxis?: string | string[]
  defaultYScale?: 'linear' | 'log'
  defaultViewSettings?: ChartViewSettings
  xMonitors: SelectOption[]
  yMonitors: SelectOption[]
  selectedCases: RunCase[]
  showRollingAverage?: boolean
  defaultWindowSize?: number
  onXAxisChange?: (row: number, col: number, xAxis: string) => void
  onYAxisChange?: (row: number, col: number, yAxis: string[]) => void
  onYScaleChange?: (row: number, col: number, yScale: 'linear' | 'log') => void
  onViewSettingsChange?: (row: number, col: number, viewSettings: ChartViewSettings) => void
  onPlotingStatesChange: (row: number, col: number, plotting: boolean) => void
  onRollingAverageChange: (row: number, col: number, showRollingAverage: boolean) => void
  onRollingAverageWindowSizeChange: (row: number, col: number, windowSize: number) => void
  onEnlargeChart?: (row: number, col: number) => void
}

const MonitorsChart = ({
  chartKey,
  xIndex,
  yIndex,
  defaultXAxis,
  defaultYAxis,
  defaultYScale = 'linear',
  defaultViewSettings,
  xMonitors,
  yMonitors,
  selectedCases,
  showRollingAverage = false,
  defaultWindowSize = 50,
  onXAxisChange,
  onYAxisChange,
  onYScaleChange,
  onViewSettingsChange,
  onPlotingStatesChange,
  onEnlargeChart,
  onRollingAverageChange,
  onRollingAverageWindowSizeChange
}: MonitorsChartProps) => {
  const appTheme = useRecoilValue(themeState)
  const [xAxis, setXAxis] = useState(defaultXAxis)
  const [yAxes, setYAxes] = useState<string[]>(() => {
    if (!defaultYAxis) return []
    return Array.isArray(defaultYAxis) ? defaultYAxis : [defaultYAxis]
  })
  const [xName, setXName] = useState(xMonitors.find(m => m.value === defaultXAxis)?.label || '')
  const [yScale, setYScale] = useState(defaultYScale)
  const [showRollingAverageState, setShowRollingAverage] = useState(showRollingAverage)
  const [windowSizeState, setWindowSize] = useState(defaultWindowSize || 50)
  const [xZoomMode, setXZoomMode] = useState<'lastn' | 'minmax'>(
    () => normalizeViewSettings(defaultViewSettings).xZoomMode
  )
  const [zoomToLast, setZoomToLast] = useState(
    () => normalizeViewSettings(defaultViewSettings).zoomToLast
  )
  const [zoomToLastInput, setZoomToLastInput] = useState(
    () => normalizeViewSettings(defaultViewSettings).zoomToLast
  )
  // Optional manual axis ranges. The *Input values update as the user types; the applied
  // values are debounced so the chart (and the heavy rolling-average recompute) only updates
  // once typing settles, which avoids the per-keystroke freeze.
  const [xMinInput, setXMinInput] = useState<number | null>(
    () => normalizeViewSettings(defaultViewSettings).xMin
  )
  const [xMaxInput, setXMaxInput] = useState<number | null>(
    () => normalizeViewSettings(defaultViewSettings).xMax
  )
  const [yMinInput, setYMinInput] = useState<number | null>(
    () => normalizeViewSettings(defaultViewSettings).yMin
  )
  const [yMaxInput, setYMaxInput] = useState<number | null>(
    () => normalizeViewSettings(defaultViewSettings).yMax
  )
  const [xMin, setXMin] = useState<number | null>(() => normalizeViewSettings(defaultViewSettings).xMin)
  const [xMax, setXMax] = useState<number | null>(() => normalizeViewSettings(defaultViewSettings).xMax)
  const [yMin, setYMin] = useState<number | null>(() => normalizeViewSettings(defaultViewSettings).yMin)
  const [yMax, setYMax] = useState<number | null>(() => normalizeViewSettings(defaultViewSettings).yMax)
  // Last view settings reported/loaded, so an unchanged snapshot (e.g. on mount) is not re-saved.
  const refLastViewSettings = useRef(JSON.stringify(normalizeViewSettings(defaultViewSettings)))

  const [{ loading: monitorDataLoading }, getMonitorData] = useGetMonitorData()
  const refMonitorData = useRef([])
  const refZoomDebounceTimer = useRef<NodeJS.Timeout | null>(null)
  const refZoomInputRef = useRef<any>(null)
  const [chartData, setChartData] = useState(null)
  const [plotting, setPlotting] = useState(false)

  // Apply manual axis ranges only after typing settles to avoid re-rendering on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setXMin(xMinInput)
      setXMax(xMaxInput)
      setYMin(yMinInput)
      setYMax(yMaxInput)
    }, 600)
    return () => clearTimeout(timer)
  }, [xMinInput, xMaxInput, yMinInput, yMaxInput])

  // Persist zoom mode and ranges with the other chart selections once the applied values change.
  useEffect(() => {
    const next = normalizeViewSettings({ xZoomMode, zoomToLast, xMin, xMax, yMin, yMax })
    const serialized = JSON.stringify(next)
    if (serialized === refLastViewSettings.current) return
    refLastViewSettings.current = serialized
    onViewSettingsChange?.(yIndex, xIndex, next)
  }, [xZoomMode, zoomToLast, xMin, xMax, yMin, yMax])
  const refRequestId = useRef(0)

  const isLogAvailable =
    yAxes.length > 0 &&
    yAxes.every(
      y =>
        y.startsWith('Continuity') ||
        y.startsWith('X_momentum') ||
        y.startsWith('Y_momentum') ||
        y.startsWith('Z_momentum') ||
        y.startsWith('Sdr') ||
        y.startsWith('Tke') ||
        y.startsWith('Energy')
    )

  // Debounce zoom to last input by 2 seconds
  useEffect(() => {
    console.log('zoomToLastInput changed:', zoomToLastInput) // Debug log
    if (zoomToLastInput === 0) {
      if (refZoomDebounceTimer.current) {
        clearTimeout(refZoomDebounceTimer.current)
      }
      setZoomToLast(0)
      return
    }

    if (refZoomDebounceTimer.current) {
      clearTimeout(refZoomDebounceTimer.current)
    }

    refZoomDebounceTimer.current = setTimeout(() => {
      console.log('Setting zoomToLast to:', zoomToLastInput) // Debug log
      setZoomToLast(zoomToLastInput)
    }, 1000)

    return () => {
      if (refZoomDebounceTimer.current) {
        clearTimeout(refZoomDebounceTimer.current)
      }
    }
  }, [zoomToLastInput])

  // Calculate max dataset length
  const maxDatasetLength = useMemo(() => {
    if (!chartData || chartData.length === 0) return 10000

    let maxLength = 0
    yAxes.forEach(yAxisVal => {
      selectedCases.forEach(caseItem => {
        const data = chartData.filter(
          d => parseInt(d.gm_id) === parseInt(caseItem.gmId) && d.monitor === yAxisVal
        )
        maxLength = Math.max(maxLength, data.length)
      })
    })

    return Math.max(maxLength, 1) // Return at least 1 to avoid 0
  }, [chartData, yAxes, selectedCases])

  const options = useMemo(() => {
    const series = []
    const seenCaseIds = new Set<string>()
    // Deduplicate selectedCases by gmId
    const uniqueCases = selectedCases.filter(c => {
      const id = String(c.gmId)
      if (seenCaseIds.has(id)) return false
      seenCaseIds.add(id)
      return true
    })
    const seenSeriesKeys = new Set<string>()
    yAxes.forEach(yAxisVal => {
      const yLabel = yMonitors.find(m => m.value === yAxisVal)?.label || yAxisVal
      uniqueCases.forEach(caseItem => {
        const key = `${yAxisVal}::${caseItem.gmId}`
        if (seenSeriesKeys.has(key)) return
        seenSeriesKeys.add(key)
        let data = chartData
          ? chartData.filter(
              d => parseInt(d.gm_id) === parseInt(caseItem.gmId) && d.monitor === yAxisVal
            )
          : []

        // Apply zoom to last filter if specified
        if (zoomToLast > 0 && data.length > zoomToLast) {
          data = data.slice(-zoomToLast)
        }

        series.push({
          type: 'line',
          id: key,
          xKey: 'x',
          yKey: 'y',
          xName: xName || 'X Axis',
          yName:
            yAxes.length > 1
              ? `${yLabel} - ${caseItem.stateName} (${caseItem.gmId})`
              : `${caseItem.stateName} (${caseItem.gmId})`,
          marker: { enabled: false },
          data
        })
      })
    })

    if (showRollingAverageState && windowSizeState > 1) {
      const rollingSeries = series.map(s => {
        const data = s.data as any[]
        const rollingData = data.map((dataItem, i) => {
          const start = Math.max(0, i - windowSizeState + 1)
          const windowSlice = data.slice(start, i + 1)
          const avgY = windowSlice.reduce((sum, item) => sum + item.y, 0) / windowSlice.length
          return { ...dataItem, y: avgY }
        })
        const rollingSeriesItem = {
          ...s,
          id: `${s.id}::rolling`,
          data: rollingData,
          yName: `${s.yName} (Rolling Avg)`
        }
        s['lineDash'] = [6, 4] // Add line dash pattern for orginal line
        return rollingSeriesItem
      })
      series.push(...rollingSeries)
    }

    const yAxisTitle =
      yAxes.length === 1
        ? yMonitors.find(m => m.value === yAxes[0])?.label || yAxes[0]
        : yAxes.map(y => yMonitors.find(m => m.value === y)?.label || y).join(', ')

    // Skip a pinned range when it would collapse/invert the domain (min >= max), which can hang
    // AG Charts' axis layout.
    const xAxisRange =
      xMin != null && xMax != null && xMin >= xMax
        ? {}
        : { ...(xMin != null ? { min: xMin } : {}), ...(xMax != null ? { max: xMax } : {}) }
    const yAxisRange =
      yMin != null && yMax != null && yMin >= yMax
        ? {}
        : { ...(yMin != null ? { min: yMin } : {}), ...(yMax != null ? { max: yMax } : {}) }

    return {
      series: series as any,
      axes: {
        x: {
          type: 'number',
          position: 'bottom',
          title: { text: xName || xAxis },
          ...xAxisRange,
          gridLine: {
            enabled: true
          },
          line: {
            enabled: true
          }
        },
        y: {
          type: yScale === 'log' ? 'log' : 'number',
          position: 'left',
          title: { text: yAxisTitle },
          ...yAxisRange,
          label: {
            enabled: true
          },
          gridLine: {
            enabled: true
          },
          line: {
            enabled: true
          }
        }
      },
      legend: { enabled: true, position: 'bottom' },
      padding: {
        top: 20,
        right: 20,
        bottom: 20,
        left: 30
      },
      zoom: {
        enabled: true,
        axes: 'xy'
      },
      theme: appTheme === 'dark' ? 'ag-default-dark' : 'ag-default'
    }
  }, [
    selectedCases,
    xName,
    chartData,
    yScale,
    xAxis,
    yAxes,
    yMonitors,
    showRollingAverageState,
    windowSizeState,
    appTheme,
    zoomToLast,
    xMin,
    xMax,
    yMin,
    yMax
  ])

  const refreshPlotData = () => {
    if (xAxis && yAxes.length > 0 && selectedCases.length > 0) {
      setPlotting(true)
      onPlotingStatesChange?.(yIndex, xIndex, true)
      const requestId = ++refRequestId.current
      const validGmIds = new Set(selectedCases.map(c => String(c.gmId)))
      const casesStr = selectedCases.map(c => c.gmId).join(',')
      Promise.all(
        yAxes.map(yAxisVal =>
          getMonitorData({ xAxis, yAxis: yAxisVal, cases: casesStr } as any).then(res => {
            return (
              res.data
                ?.filter(
                  item =>
                    ((item.physical_time_s !== null && xAxis === 'physical_time_s') ||
                      (item.iteration !== null && xAxis === 'iteration')) &&
                    item.monitor_value_double !== null &&
                    validGmIds.has(String(item.gm_id))
                )
                .map(item => ({
                  x:
                    xAxis === 'physical_time_s'
                      ? parseFloat(item.physical_time_s)
                      : parseInt(item.iteration),
                  y: parseFloat(item.monitor_value_double),
                  gm_id: item.gm_id,
                  monitor: yAxisVal
                })) || []
            )
          })
        )
      )
        .then(results => {
          if (requestId !== refRequestId.current) return
          const allData = results.flat()
          refMonitorData.current = allData
          setChartData(allData)
        })
        .catch(error => {
          console.error('Error fetching monitor data:', error)
        })
        .finally(() => {
          if (requestId === refRequestId.current) {
            setPlotting(false)
            onPlotingStatesChange?.(yIndex, xIndex, false)
          }
        })
    }
  }

  useEffect(() => {
    if (xAxis && yAxes.length > 0 && selectedCases.length > 0) {
      // Immediately filter chartData to only include current cases and monitors
      if (chartData) {
        const validGmIds = new Set(selectedCases.map(c => String(c.gmId)))
        const validMonitors = new Set(yAxes)
        const filtered = chartData.filter(
          d => validGmIds.has(String(d.gm_id)) && validMonitors.has(d.monitor)
        )
        if (filtered.length !== chartData.length) {
          setChartData(filtered)
        }
      }
      refreshPlotData()
    } else {
      setChartData(null)
    }
  }, [xAxis, yAxes, selectedCases])

  const handGetPlotData = () => {
    refreshPlotData()
  }

  return (
    <div className="h-full flex flex-col items-stretch justify-center p-4 pt-2 px-2">
      <div className="flex items-center px-0 space-x-2 flex-wrap">
        <div className="flex items-center">
          <label className="mr-2">X:</label>
          <Dropdown
            value={xAxis}
            disabled={selectedCases.length === 0}
            defaultValue={defaultXAxis}
            options={xMonitors}
            onChange={e => {
              setXAxis(e.value)
              setXName(xMonitors.find(m => m.value === e.value)?.label || '')
              onXAxisChange?.(yIndex, xIndex, e.value)
            }}
            placeholder="Select..."
            scrollHeight="40rem"
            pt={{
              root: { className: 'p-0 max-w-[120px]' },
              input: { className: '!p-[4px] text-sm' },
              trigger: { className: 'w-8' }
            }}
          />
        </div>
        <div className="flex items-center">
          <label className="mr-2">Y:</label>
          <MultiSelect
            value={yAxes}
            filter
            options={yMonitors}
            disabled={selectedCases.length === 0}
            onChange={e => {
              const newYAxes = e.value as string[]
              setYAxes(newYAxes)
              onYAxisChange?.(yIndex, xIndex, newYAxes)
              const allSupportsLog =
                newYAxes.length > 0 &&
                newYAxes.every(
                  v =>
                    v.startsWith('Continuity') ||
                    v.startsWith('X_momentum') ||
                    v.startsWith('Y_momentum') ||
                    v.startsWith('Z_momentum') ||
                    v.startsWith('Sdr') ||
                    v.startsWith('Tke') ||
                    v.startsWith('Energy')
                )
              if (yScale === 'log' && !allSupportsLog) {
                setYScale('linear')
                onYScaleChange?.(yIndex, xIndex, 'linear')
              }
            }}
            placeholder="Select..."
            scrollHeight="40rem"
            maxSelectedLabels={2}
            pt={{
              root: { className: 'p-0 max-w-[200px]' },
              label: { className: '!p-[4px] text-sm' },
              trigger: { className: 'w-8' }
            }}
          />
        </div>
        {isLogAvailable && (
          <div className="flex items-center mt-0">
            <SelectButton
              value={isLogAvailable ? yScale : 'linear'}
              unselectable={false}
              options={[
                { label: 'Linear', value: 'linear' },
                { label: 'Log', value: 'log' }
              ]}
              onChange={e => {
                setYScale(e.value)
                onYScaleChange?.(yIndex, xIndex, e.value)
              }}
              pt={{
                root: { className: 'p-0' },
                button: {
                  className: '!p-[4px] !px-2 text-sm focus:!shadow-none !outline-none'
                }
              }}
            />
          </div>
        )}

        <div className="flex items-center space-x-2">
          <div className="flex items-center mt-0">
            <InputSwitch
              disabled={selectedCases.length === 0}
              checked={showRollingAverageState}
              onChange={e => {
                setShowRollingAverage(e.value)
                onRollingAverageChange(yIndex, xIndex, e.value)
              }}
              pt={{
                root: {
                  className: '!h-[24px] w-12'
                },
                slider: {
                  className: 'h-[24px] !w-12 dark:!bg-neutral-10'
                }
              }}
            />

            <label className="ml-1 text-sm mr-1 pt-1" htmlFor="showRollingAverage">
              Rolling Avg
            </label>
          </div>

          {showRollingAverageState && (
            <div className="flex items-center pt-0.5 mt-0">
              <label className="mr-2 text-sm">Window Size:</label>
              <InputNumber
                value={windowSizeState}
                disabled={selectedCases.length === 0}
                onValueChange={e => {
                  const val = e.value ?? 50
                  setWindowSize(val)
                  onRollingAverageWindowSizeChange(yIndex, xIndex, val)
                }}
                min={1}
                max={10000}
                useGrouping={false}
                pt={{
                  root: { className: 'max-w-[40px]' },
                  input: {
                    root: { className: '!px-0.5 pt-3 text-center text-sm w-full max-h-[26px]' }
                  }
                }}
              />
            </div>
          )}
          <SelectButton
            value={xZoomMode}
            unselectable={false}
            options={[
              { label: 'Last N', value: 'lastn' },
              { label: 'Min/Max', value: 'minmax' }
            ]}
            onChange={e => {
              // Handle X Zoom Mode change
              setXZoomMode(e.value)
            }}
            pt={{
              root: { className: 'p-0' },
              button: {
                className: '!p-[4px] !px-2 text-sm focus:!shadow-none !outline-none'
              }
            }}
          />

          <div className="flex items-center gap-3 pt-0.5">
            {xZoomMode === 'lastn' && (
              <div className="flex items-center gap-1 pt-0.5 mt-0 pl-1">
                <label className="mr-2 text-sm">Zoom to Last:</label>
                <InputNumber
                  ref={refZoomInputRef}
                  min={1}
                  max={maxDatasetLength}
                  value={zoomToLastInput}
                  maxFractionDigits={0}
                  disabled={selectedCases.length === 0}
                  onChange={e => {
                    const val = Number(e.value) || 0
                    console.log(
                      'Zoom to Last Input Changed from onChange:',
                      e.value,
                      'parsed to:',
                      val
                    ) // Debug log
                    setZoomToLastInput(val)
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      const inputVal = parseInt(refZoomInputRef.current?.value) || 0
                      if (inputVal > 0) {
                        e.preventDefault()
                        if (refZoomDebounceTimer.current) {
                          clearTimeout(refZoomDebounceTimer.current)
                          refZoomDebounceTimer.current = null
                        }
                        setZoomToLastInput(inputVal)
                        setZoomToLast(inputVal)
                      }
                    }
                  }}
                  pt={{
                    root: { className: 'max-w-[60px]' },
                    input: {
                      root: { className: '!px-0.5 pt-3 text-center text-sm w-full max-h-[26px]' }
                    }
                  }}
                />
              </div>
            )}
            {xZoomMode === 'minmax' && (
              <div className="flex items-center gap-1">
                <label className="text-sm">X Range:</label>
                <InputNumber
                  value={xMinInput}
                  disabled={selectedCases.length === 0}
                  onValueChange={e => setXMinInput(e.value ?? null)}
                  placeholder="min"
                  maxFractionDigits={6}
                  useGrouping={false}
                  pt={{
                    root: { className: 'max-w-[60px]' },
                    input: {
                      root: { className: '!px-0.5 pt-3 text-center text-sm w-full max-h-[26px]' }
                    }
                  }}
                />
                <span className="text-sm">-</span>
                <InputNumber
                  value={xMaxInput}
                  disabled={selectedCases.length === 0}
                  onValueChange={e => setXMaxInput(e.value ?? null)}
                  placeholder="max"
                  maxFractionDigits={6}
                  useGrouping={false}
                  pt={{
                    root: { className: 'max-w-[60px]' },
                    input: {
                      root: { className: '!px-0.5 pt-3 text-center text-sm w-full max-h-[26px]' }
                    }
                  }}
                />
              </div>
            )}
            <div className="flex items-center gap-1">
              <label className="text-sm">Y Range:</label>
              <InputNumber
                value={yMinInput}
                disabled={selectedCases.length === 0}
                onValueChange={e => setYMinInput(e.value ?? null)}
                placeholder="min"
                maxFractionDigits={6}
                useGrouping={false}
                pt={{
                  root: { className: 'max-w-[60px]' },
                  input: {
                    root: { className: '!px-0.5 pt-3 text-center text-sm w-full max-h-[26px]' }
                  }
                }}
              />
              <span className="text-sm">-</span>
              <InputNumber
                value={yMaxInput}
                disabled={selectedCases.length === 0}
                onValueChange={e => setYMaxInput(e.value ?? null)}
                placeholder="max"
                maxFractionDigits={6}
                useGrouping={false}
                pt={{
                  root: { className: 'max-w-[60px]' },
                  input: {
                    root: { className: '!px-0.5 pt-3 text-center text-sm w-full max-h-[26px]' }
                  }
                }}
              />
            </div>
          </div>
        </div>

        <Button
          label="Plot"
          loading={monitorDataLoading}
          disabled={!xAxis || yAxes.length === 0 || monitorDataLoading}
          className="hidden p-button-sm p-button-outlined text-sm disabled:opacity-50 disabled:cursor-not-allowed mt-1"
          onClick={handGetPlotData}
        />
      </div>
      <div className="chart-container basis-0 grow min-h-0 w-full flex items-center justify-center relative mt-4">
        {chartData && chartData.length > 0 ? (
          <div className="w-full h-full">
            <AgCharts key={`chart-${chartKey}-${yScale}`} options={options as any} />
          </div>
        ) : (
          <p className="p-10 text-center">
            No data to display.
            <br />
            {selectedCases.length === 0
              ? 'Please select at least one case to view data.'
              : !chartData
                ? 'Please select X and Y axes.'
                : chartData.length === 0
                  ? 'Chart data is empty.'
                  : ''}
          </p>
        )}
        {plotting && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
            <ProgressSpinner className="max-w-8 max-h-8 text-gm-blue" />
          </div>
        )}
      </div>
    </div>
  )
}

export default MonitorsChart
